import type { ReactiveController, ReactiveControllerHost } from "lit";

export type LayoutType = "mobile" | "tablet" | "desktop" | "ultrawide";

export interface LayoutBreakpoints {
  mobile: number; // Default: 768
  tablet: number; // Default: 1024
  ultrawide: number; // Default: 2100
}

/**
 * Reactive controller that tracks window size changes and provides layout type information.
 * Follows the established reactive controller patterns in the WOW platform.
 */
export class LayoutController implements ReactiveController {
  private _currentLayout: LayoutType = "desktop";
  private breakpoints: LayoutBreakpoints;

  constructor(
    private host: ReactiveControllerHost,
    breakpoints?: Partial<LayoutBreakpoints>,
  ) {
    this.host.addController(this);
    this.breakpoints = {
      mobile: 768,
      tablet: 1024,
      ultrawide: 2100,
      ...breakpoints,
    };
  }

  /**
   * Current layout type based on window width
   */
  get layout(): LayoutType {
    return this._currentLayout;
  }

  /**
   * True if current layout is mobile
   */
  get isMobile(): boolean {
    return this._currentLayout === "mobile";
  }

  /**
   * True if current layout is tablet
   */
  get isTablet(): boolean {
    return this._currentLayout === "tablet";
  }

  /**
   * True if current layout is desktop
   */
  get isDesktop(): boolean {
    return this._currentLayout === "desktop" || this._currentLayout === "ultrawide";
  }

  /**
   * True if current layout is ultra-wide (beyond desktop)
   */
  get isUltrawide(): boolean {
    return this._currentLayout === "ultrawide";
  }

  /**
   * CSS classes for layout-specific styling
   */
  get layoutClasses(): Record<string, boolean> {
    return {
      "layout-mobile": this.isMobile,
      "layout-tablet": this.isTablet,
      "layout-desktop": this.isDesktop,
      "layout-ultra-wide": this.isUltrawide,
    };
  }

  /**
   * Media query strings for CSS
   */
  get mediaQueries() {
    return {
      mobile: `(max-width: ${this.breakpoints.mobile - 1}px)`,
      tablet: `(min-width: ${this.breakpoints.mobile}px) and (max-width: ${this.breakpoints.tablet - 1}px)`,
      desktop: `(min-width: ${this.breakpoints.tablet}px)`,
      ultrawide: `(min-width: ${this.breakpoints.ultrawide}px)`,
    };
  }

  hostConnected(): void {
    this.updateLayout();
    window.addEventListener("resize", this.updateLayout);
  }

  hostDisconnected(): void {
    window.removeEventListener("resize", this.updateLayout);
  }

  private updateLayout = (): void => {
    const width = window.innerWidth;
    let newLayout: LayoutType;

    if (width < this.breakpoints.mobile) {
      newLayout = "mobile";
    } else if (width < this.breakpoints.tablet) {
      newLayout = "tablet";
    } else if (width >= this.breakpoints.ultrawide) {
      newLayout = "ultrawide";
    } else {
      newLayout = "desktop";
    }

    if (newLayout !== this._currentLayout) {
      this._currentLayout = newLayout;
      this.host.requestUpdate();
    }
  };
}
